import { useEffect, useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Modal } from '../../../components/ui/Modal'
import { useRangeSelection } from '../../../lib/list/useRangeSelection'
import { formatPrice } from '../format'
import type { AddPartsSearchResult } from '../types'

export interface AddPartsModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onAdd: (resultIds: readonly string[]) => Promise<void>
  readonly search: (query: string) => Promise<readonly AddPartsSearchResult[]>
}

/**
 * Add-parts modal (screens.md §7 / components.md "Modal") — search spans
 * the parts library and the standard catalog (`GET
 * /api/boms/add-parts/search`); empty state per components.md's BOM-detail
 * row ("No parts match your search").
 */
/** Search debounce (code-review S3) — before the real BFF swap, this
 * fires a query per keystroke. */
const SEARCH_DEBOUNCE_MS = 300

export function AddPartsModal({ open, onClose, onAdd, search }: AddPartsModalProps) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState<readonly AddPartsSearchResult[]>([])
  // UID-610 — `useRangeSelection` additionally carries the Gmail/Finder
  // shift-click anchor.
  const rangeSelection = useRangeSelection()
  const { selectedIds: selected } = rangeSelection
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    search(debouncedQuery).then((rows) => {
      if (!cancelled) setResults(rows)
    })
    return () => {
      cancelled = true
    }
  }, [open, debouncedQuery, search])

  // biome-ignore lint/correctness/useExhaustiveDependencies: rangeSelection.clear closes over the same stable setState every render, so omitting it doesn't risk a stale write.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setDebouncedQuery('')
      rangeSelection.clear()
    }
  }, [open])

  /** UID-610 — shift-click range selection over the currently rendered
   * search-result order (Gmail/Finder semantics, same as UID-470's Parts
   * Library). `shiftKey` reaches here from the row checkbox's `onClick`,
   * not `onChange` (a `ChangeEvent` carries no modifier-key state). */
  function toggle(id: string, shiftKey: boolean) {
    rangeSelection.toggle(
      id,
      results.map((r) => r.id),
      shiftKey,
    )
  }

  async function handleAdd() {
    setBusy(true)
    try {
      await onAdd([...selected])
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add parts"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleAdd} loading={busy} disabled={selected.size === 0}>
            Add {selected.size > 0 ? selected.size : ''} part{selected.size === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <label className="sr-only" htmlFor="add-parts-search">
        Search parts and catalog
      </label>
      <input
        id="add-parts-search"
        type="search"
        className="parts-search-input"
        placeholder="Search library + catalog"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {results.length === 0 ? (
        <EmptyState title="No parts match your search" />
      ) : (
        <ul className="add-parts-results">
          {results.map((result) => (
            <li key={result.id} className="add-parts-result-row">
              <label>
                <input
                  type="checkbox"
                  checked={selected.has(result.id)}
                  // UID-610 — reads `event.shiftKey` off `onClick`;
                  // `onChange` carries no modifier-key state, so it
                  // stays a no-op (see `toggle`'s doc comment).
                  onClick={(event) => toggle(result.id, event.shiftKey)}
                  onChange={() => {}}
                />
                {result.name}
                <span className="caption-text"> · {result.source}</span>
              </label>
              {result.price && <span>{formatPrice(result.price)}</span>}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
